/**
 * Improvement Parameters Editor
 * 
 * Component for editing process improvement tracking parameters
 */

import React from 'react';
import { Card, Select, InputNumber, Input, DatePicker, Space, Button, Progress, Tag, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { ImprovementParameters, ImprovementParameter } from '../../types/processParameters';
import dayjs from 'dayjs';
import './ImprovementParametersEditor.css';

const { TextArea } = Input;

interface ImprovementParametersEditorProps {
  improvements: ImprovementParameters;
  onChange: (improvements: ImprovementParameters) => void;
}

export const ImprovementParametersEditor: React.FC<ImprovementParametersEditorProps> = ({
  improvements,
  onChange
}) => {
  const handleImprovementChange = (improvementName: string, field: keyof ImprovementParameter, value: any) => {
    onChange({
      ...improvements,
      [improvementName]: {
        ...improvements[improvementName as keyof ImprovementParameters],
        [field]: value
      } as ImprovementParameter
    });
  };

  const handleAddImprovement = () => {
    const name = prompt('Enter improvement name (e.g., lead_time_reduction):');
    if (!name) return;
    
    onChange({
      ...improvements,
      [name]: {
        parameter_type: 'REDUCTION',
        status: 'PLANNED',
        priority: 'MEDIUM'
      } as ImprovementParameter
    });
  };

  const handleDeleteImprovement = (improvementName: string) => {
    const updated = { ...improvements };
    delete updated[improvementName as keyof ImprovementParameters];
    onChange(updated);
  };

  // Predefined improvements
  const predefinedImprovements = [
    'cycle_time_reduction',
    'defect_reduction',
    'yield_improvement',
    'cost_reduction',
    'waste_elimination',
    'capacity_increase',
    'throughput_improvement'
  ];

  // Get all improvement names
  const improvementNames = Object.keys(improvements);
  const customImprovements = improvementNames.filter(name => !predefinedImprovements.includes(name));

  const renderImprovementCard = (improvementName: string, improvement: ImprovementParameter) => {
    // Calculate progress if values are available
    let progress = 0;
    if (improvement.baseline_value && improvement.target_value && improvement.actual_value !== undefined) {
      const range = improvement.target_value - improvement.baseline_value;
      const achieved = improvement.actual_value - improvement.baseline_value;
      progress = range !== 0 ? Math.round((achieved / range) * 100) : 0;
    }

    const statusColor = {
      'PLANNED': 'default',
      'IN_PROGRESS': 'processing',
      'COMPLETED': 'success',
      'CANCELLED': 'error'
    }[improvement.status || 'PLANNED'];

    return (
      <Card
        key={improvementName}
        size="small"
        title={
          <Space>
            <span style={{ textTransform: 'capitalize' }}>
              {improvementName.replace(/_/g, ' ')}
            </span>
            {improvement.status && <Tag color={statusColor}>{improvement.status}</Tag>}
            {improvement.priority && (
              <Tag color={improvement.priority === 'CRITICAL' ? 'red' : improvement.priority === 'HIGH' ? 'orange' : 'default'}>
                {improvement.priority}
              </Tag>
            )}
          </Space>
        }
        extra={
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteImprovement(improvementName)}
          />
        }
        style={{ marginBottom: 16 }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          {/* Type and Status */}
          <Space style={{ width: '100%' }} wrap>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: '12px' }}>Type:</label>
              <Select
                style={{ width: 150 }}
                size="small"
                value={improvement.parameter_type}
                onChange={(value) => handleImprovementChange(improvementName, 'parameter_type', value)}
              >
                <Select.Option value="REDUCTION">Reduction</Select.Option>
                <Select.Option value="INCREASE">Increase</Select.Option>
                <Select.Option value="OPTIMIZATION">Optimization</Select.Option>
                <Select.Option value="ELIMINATION">Elimination</Select.Option>
              </Select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: '12px' }}>Status:</label>
              <Select
                style={{ width: 150 }}
                size="small"
                value={improvement.status}
                onChange={(value) => handleImprovementChange(improvementName, 'status', value)}
              >
                <Select.Option value="PLANNED">Planned</Select.Option>
                <Select.Option value="IN_PROGRESS">In Progress</Select.Option>
                <Select.Option value="COMPLETED">Completed</Select.Option>
                <Select.Option value="CANCELLED">Cancelled</Select.Option>
              </Select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: '12px' }}>Priority:</label>
              <Select
                style={{ width: 120 }}
                size="small"
                value={improvement.priority}
                onChange={(value) => handleImprovementChange(improvementName, 'priority', value)}
              >
                <Select.Option value="LOW">Low</Select.Option>
                <Select.Option value="MEDIUM">Medium</Select.Option>
                <Select.Option value="HIGH">High</Select.Option>
                <Select.Option value="CRITICAL">Critical</Select.Option>
              </Select>
            </div>
          </Space>

          {/* Description */}
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: '12px' }}>Description:</label>
            <TextArea
              rows={2}
              size="small"
              value={improvement.description}
              onChange={(e) => handleImprovementChange(improvementName, 'description', e.target.value)}
              placeholder="Describe the improvement initiative"
            />
          </div>

          {/* Values */}
          <Divider style={{ margin: '8px 0' }}>Target Values</Divider>
          <Space style={{ width: '100%' }} wrap>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: '12px' }}>Baseline:</label>
              <InputNumber
                size="small"
                value={improvement.baseline_value}
                onChange={(value) => handleImprovementChange(improvementName, 'baseline_value', value)}
                placeholder="Current"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: '12px' }}>Target:</label>
              <InputNumber
                size="small"
                value={improvement.target_value}
                onChange={(value) => handleImprovementChange(improvementName, 'target_value', value)}
                placeholder="Goal"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: '12px' }}>Actual:</label>
              <InputNumber
                size="small"
                value={improvement.actual_value}
                onChange={(value) => handleImprovementChange(improvementName, 'actual_value', value)}
                placeholder="Achieved"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: '12px' }}>Unit:</label>
              <Input
                size="small"
                style={{ width: 100 }}
                value={improvement.unit}
                onChange={(e) => handleImprovementChange(improvementName, 'unit', e.target.value)}
                placeholder="e.g., hours"
              />
            </div>
          </Space>

          {/* Progress bar */}
          {progress !== 0 && (
            <div style={{ marginTop: 8 }}>
              <Progress
                percent={progress}
                status={progress >= 100 ? 'success' : progress > 0 ? 'active' : 'normal'}
                size="small"
              />
            </div>
          )}

          {/* Dates */}
          <Divider style={{ margin: '8px 0' }}>Timeline</Divider>
          <Space style={{ width: '100%' }} wrap>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: '12px' }}>Start Date:</label>
              <DatePicker
                size="small"
                value={improvement.start_date ? dayjs(improvement.start_date) : null}
                onChange={(date) => handleImprovementChange(improvementName, 'start_date', date?.toISOString())}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: '12px' }}>Target Date:</label>
              <DatePicker
                size="small"
                value={improvement.target_date ? dayjs(improvement.target_date) : null}
                onChange={(date) => handleImprovementChange(improvementName, 'target_date', date?.toISOString())}
              />
            </div>

            {improvement.status === 'COMPLETED' && (
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: '12px' }}>Completion Date:</label>
                <DatePicker
                  size="small"
                  value={improvement.completion_date ? dayjs(improvement.completion_date) : null}
                  onChange={(date) => handleImprovementChange(improvementName, 'completion_date', date?.toISOString())}
                />
              </div>
            )}
          </Space>

          {/* Owner */}
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: '12px' }}>Owner:</label>
            <Input
              size="small"
              value={improvement.owner}
              onChange={(e) => handleImprovementChange(improvementName, 'owner', e.target.value)}
              placeholder="Person or team responsible"
            />
          </div>

          {/* Expected Benefit */}
          <Divider style={{ margin: '8px 0' }}>Expected Benefit</Divider>
          <Space>
            <InputNumber
              size="small"
              value={improvement.expected_benefit}
              onChange={(value) => handleImprovementChange(improvementName, 'expected_benefit', value)}
              placeholder="Value"
            />
            <Input
              size="small"
              style={{ width: 100 }}
              value={improvement.expected_benefit_unit}
              onChange={(e) => handleImprovementChange(improvementName, 'expected_benefit_unit', e.target.value)}
              placeholder="Unit"
            />
          </Space>
        </Space>
      </Card>
    );
  };

  return (
    <div className="improvement-parameters-editor">
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Render all improvements */}
        {improvementNames.map(name => {
          const improvement = improvements[name as keyof ImprovementParameters];
          if (!improvement) return null;
          return renderImprovementCard(name, improvement);
        })}

        {/* Empty state */}
        {improvementNames.length === 0 && (
          <Card size="small">
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#888' }}>
              No improvements defined. Click below to add an improvement initiative.
            </div>
          </Card>
        )}

        {/* Add button */}
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={handleAddImprovement}
          style={{ width: '100%' }}
        >
          Add Improvement Initiative
        </Button>
      </Space>
    </div>
  );
};

